/** Quick create category/subcategory dialog.
 *
 * Простая модалка: name + auto-slug (lockable). На submit — POST на
 * /categories с translations: [{language:"ru", name}] и переданным
 * parent_id (если subcategory).
 */

import { useMutation } from "@tanstack/react-query";
import { Loader2, Lock, Unlock } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { slugify } from "@/lib/slugify";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Если задан — создаём как подкатегорию этой. */
  parentId?: number | null;
  parentName?: string;
  onCreated?: (newId: number) => void;
}

export function InlineCreateDialog({
  open,
  onClose,
  parentId,
  parentName,
  onCreated,
}: Props): JSX.Element {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugLocked, setSlugLocked] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!open) return;
    setName("");
    setSlug("");
    setSlugLocked(false);
    setIsVisible(true);
  }, [open]);

  // Auto-slug
  useEffect(() => {
    if (!slugLocked) {
      setSlug(slugify(name));
    }
  }, [name, slugLocked]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        slug: slug.trim(),
        parent_id: parentId ?? null,
        is_visible: isVisible,
        sort_order: 0,
        translations: [{ language: "ru", name: name.trim() }],
      };
      const { data } = await api.post("/categories", payload);
      return data as { id: number };
    },
    onSuccess: (data) => {
      toast.success(
        parentId
          ? `Подкатегория «${name}» создана`
          : `Категория «${name}» создана`,
      );
      onClose();
      onCreated?.(data.id);
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Ошибка");
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {parentId
              ? `Новая подкатегория${parentName ? ` в «${parentName}»` : ""}`
              : "Новая категория"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Название *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Кухонные столы"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-2">
              Slug
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                title={
                  slugLocked
                    ? "Включить автогенерацию"
                    : "Заморозить ручное значение"
                }
                onClick={() => setSlugLocked((v) => !v)}
              >
                {slugLocked ? (
                  <Lock className="w-3 h-3" />
                ) : (
                  <Unlock className="w-3 h-3" />
                )}
              </button>
            </Label>
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugLocked(true);
              }}
              placeholder="kuhonnye-stoly"
              className="font-mono"
            />
            <p className="text-[10px] text-muted-foreground">
              {slugLocked
                ? "Slug заморожен — переключите замок чтобы вернуть автогенерацию"
                : "Slug генерируется автоматически из названия"}
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Switch
              checked={isVisible}
              onCheckedChange={(v) => setIsVisible(v)}
            />
            Видна на сайте
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={
              !name.trim() || !slug.trim() || createMutation.isPending
            }
          >
            {createMutation.isPending && (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            )}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
